import React, { PureComponent } from "react";
import {
  TableWrapper,
  StyledTable,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  PaginationWrapper,
} from "./styledComponents";
import RowItem from "./RowItem";
import { compare } from "utils/helpers";
import Icon from "../Icon";
import Loader from "components/Loader";
import { colors } from "globalStyles";

const Pagination = ({ pageList, currentPage, pageCount, onPageChange }) => {
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === pageCount;

  return pageList ? (
    <PaginationWrapper>
      <button
        type="button"
        className={`first-btn ${isFirstPage ? "disabled" : ""}`}
        disabled={isFirstPage}
        onClick={() => !isFirstPage && onPageChange(1)}
      >
        First
      </button>
      <button
        type="button"
        className={`prev ${isFirstPage ? "disabled" : ""}`}
        aria-label="Previous page"
        disabled={isFirstPage}
        onClick={() =>
          !isFirstPage &&
          onPageChange(currentPage - 2 > 1 ? currentPage - 2 : 1)
        }
      >
        <Icon name="drop-down" size="12" />
      </button>
      {pageList.map((item, idx) => (
        <button
          type="button"
          key={idx}
          className={`page-btn ${item.value === currentPage ? "selected" : ""}`}
          aria-label={`Go to page ${item.value}`}
          aria-current={item.value === currentPage ? "page" : undefined}
          onClick={() => onPageChange(item.value)}
        >
          {item.label}
        </button>
      ))}
      <button
        type="button"
        className={`next ${isLastPage ? "disabled" : ""}`}
        aria-label="Next page"
        disabled={isLastPage}
        onClick={() =>
          !isLastPage &&
          onPageChange(
            currentPage + 2 > pageCount ? pageCount : currentPage + 2
          )
        }
      >
        <Icon name="drop-down" size="12" />
      </button>
      <button
        type="button"
        className={`last-btn ${isLastPage ? "disabled" : ""}`}
        disabled={isLastPage}
        onClick={() => !isLastPage && onPageChange(pageCount)}
      >
        Last
      </button>
    </PaginationWrapper>
  ) : null;
};

export default class Table extends PureComponent {
  state = {
    sort: null,
  };

  handleSort(name, noSort) {
    const { sort } = this.state;
    if (!noSort) {
      const sortObj = {
        path: name,
        isDesc: !sort || sort.path !== name ? false : !sort.isDesc,
      };
      this.setState({ sort: sortObj }, () => {
        const { sortChanged } = this.props;
        if (sortChanged) sortChanged(sortObj);
      });
    }
  }

  getData = () => {
    var data =
      this.props.data && Array.isArray(this.props.data) ? this.props.data : [];

    return data;
  };

  generatePaginationList = () => {
    const { currentPage, pageCount } = this.props;
    var result = [];
    if (pageCount - currentPage < 5) {
      while (pageCount - result.length >= 1 && result.length <= 5) {
        const pageValue = pageCount - result.length;
        result.unshift({ label: pageValue, value: pageValue });
      }
    } else {
      result = [
        { label: currentPage, value: currentPage },
        { label: currentPage + 1, value: currentPage + 1 },
        { label: "...", value: currentPage + 2 },
        { label: pageCount - 1, value: pageCount - 1 },
        { label: pageCount, value: pageCount },
      ];
    }

    return result;
  };

  render() {
    const {
      columns,
      currentPage,
      pageCount,
      onPageChange,
      isFetching,
      isLeadsTable,
      onRowClick,
    } = this.props;
    const { sort } = this.state;
    var data = this.getData();
    if (!columns) return;

    return (
      <TableWrapper className="table-wrapper" isLeadsTable={isLeadsTable}>
        <StyledTable>
          <TableHead>
            <TableRow>
              {columns.map((c, idx) => {
                const isSort = sort && sort.path === c.path;
                // A sortable header must be a real <button> (keyboard +
                // accessible name from its text); non-sortable and custom
                // headers stay plain so no nameless button is emitted.
                const sortable = !c.noSort && !c.headerCustom && c.header;
                const content = (
                  <div className={isSort ? "sorted" : ""}>
                    {c.headerCustom
                      ? c.headerCustom(this.handleSort)
                      : c.header}
                    {isSort && (
                      <Icon
                        name="drop-down"
                        size="10"
                        className={sort.isDesc ? "sort-desc-icon" : ""}
                      />
                    )}
                  </div>
                );
                return (
                  <TableHeader
                    key={idx}
                    width={c.width}
                    aria-sort={
                      sortable && isSort
                        ? sort.isDesc
                          ? "descending"
                          : "ascending"
                        : undefined
                    }
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => this.handleSort(c.path, c.noSort)}
                      >
                        {content}
                      </button>
                    ) : (
                      content
                    )}
                  </TableHeader>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <tr className="loader">
                <td colSpan={10}>
                  <Loader color={colors.MAIN_COLOR} size="large" />
                </td>
              </tr>
            ) : (
              data.map((r, idx) => (
                <RowItem
                  key={idx}
                  columns={columns}
                  data={r}
                  isEven={idx % 2 === 0}
                  isLeadsTable={isLeadsTable}
                  onRowClick={onRowClick}
                />
              ))
            )}
          </TableBody>
        </StyledTable>
        {onRowClick && (
          // Announced via each row's aria-describedby: tells assistive tech
          // the focusable rows are actionable without replacing their
          // implicit row role (which an aria-label or button role would).
          <span id="table-row-action-hint" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
            Press Enter to open
          </span>
        )}
        {currentPage && pageCount && onPageChange && pageCount !== 1 && (
          <Pagination
            pageList={this.generatePaginationList()}
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={onPageChange}
          />
        )}
      </TableWrapper>
    );
  }
}
